import { Field as ChakraField, FieldLabel, Flex, FlexProps } from '@chakra-ui/react';
import { forwardRef } from 'react';

type ActionTypes = 'primary' | 'cancel' | 'new';

const typeColor: Record<ActionTypes, string> = {
    primary: 'purple',
    cancel: 'red',
    new: 'blue',
};
export interface FieldProps extends Omit<ChakraField.RootProps, 'label'> {
    label?: React.ReactNode;
    helperText?: React.ReactNode;
    errorText?: React.ReactNode;
    optionalText?: React.ReactNode;
    actions?: {
        label: string;
        onClick: () => void;
        disabled?: boolean;
        type: ActionTypes;
    }[];
}

export const Field = forwardRef<HTMLDivElement, FieldProps>(function Field(props, ref) {
    const { label, children, helperText, errorText, optionalText, actions, ...rest } = props;
    return (
        <ChakraField.Root ref={ref} {...rest}>
            {label &&
                (actions ? (
                    <Flex justifyContent="space-between" width="100%">
                        <ChakraField.Label>
                            {label}
                            <ChakraField.RequiredIndicator fallback={optionalText} />
                        </ChakraField.Label>
                        <Flex gap="3">
                            {actions.map(
                                (action) =>
                                    !action.disabled && (
                                        <FieldLabel
                                            key={action.label}
                                            onClick={action.onClick}
                                            fontWeight="bold"
                                            color={`${typeColor[action.type]}.300`}
                                            _hover={{
                                                color: `${typeColor[action.type]}.500`,
                                                textDecor: 'underline',
                                                cursor: 'pointer',
                                            }}>
                                            {action.label}
                                        </FieldLabel>
                                    ),
                            )}
                        </Flex>
                    </Flex>
                ) : (
                    <ChakraField.Label>
                        {label}
                        <ChakraField.RequiredIndicator fallback={optionalText} />
                    </ChakraField.Label>
                ))}
            {children}
            {helperText && <ChakraField.HelperText>{helperText}</ChakraField.HelperText>}
            {errorText && <ChakraField.ErrorText>{errorText}</ChakraField.ErrorText>}
        </ChakraField.Root>
    );
});

export const FieldGroup = forwardRef<HTMLDivElement, FlexProps>(function FieldGroup(props, ref) {
    const { children, ...rest } = props;
    return (
        <Flex ref={ref} flexDirection="column" gap="16px" w="100%" {...rest}>
            {children}
        </Flex>
    );
});

export const FieldRow = forwardRef<HTMLDivElement, FlexProps>(function FieldRow(props, ref) {
    const { children, ...rest } = props;
    return (
        <Flex ref={ref} flexDirection="row" gap="16px" w="100%" flexWrap={{ base: 'wrap', lg: 'nowrap' }} {...rest}>
            {children}
        </Flex>
    );
});
